import type React from "react"
import { useRef } from "react"

import { Textarea } from "@/components/ui/textarea"
import { parseTextSegments } from "@/lib/text-segments"
import { cn } from "@/lib/utils"

function SegmentSpan({ type, value }: { type: string; value: string }) {
  if (type === "mention" || type === "hashtag") {
    return <span className="text-muted-foreground">{value}</span>
  }
  if (type === "url") {
    return (
      <span className="text-primary underline underline-offset-2">{value}</span>
    )
  }
  return <>{value}</>
}

type TextareaProps = React.ComponentProps<typeof Textarea> & {
  textareaRef?: React.Ref<HTMLTextAreaElement>
}

export default function HighlightedTextarea({
  value,
  className,
  onScroll,
  textareaRef,
  ...props
}: TextareaProps) {
  const backdropRef = useRef<HTMLDivElement>(null)
  const text = String(value ?? "")
  const segments = parseTextSegments(text)

  function handleScroll(e: React.UIEvent<HTMLTextAreaElement>) {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop
      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft
    }
    onScroll?.(e)
  }

  return (
    <div className="relative">
      <div
        ref={backdropRef}
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 field-sizing-content overflow-hidden rounded-md border border-transparent px-3 py-2 text-base break-words whitespace-pre-wrap md:text-sm",
          className,
        )}
      >
        {segments.map((segment, i) => (
          <SegmentSpan key={i} type={segment.type} value={segment.value} />
        ))}
        {text.endsWith("\n") && " "}
      </div>

      <Textarea
        ref={textareaRef}
        value={value}
        onScroll={handleScroll}
        className={cn(
          text.length > 0
            ? "caret-foreground bg-transparent text-transparent selection:bg-blue-200/50 selection:text-transparent"
            : "bg-transparent",
          className,
        )}
        {...props}
      />
    </div>
  )
}
